import { useState } from "react";
import { Link } from "react-router-dom";
import { Truck, PackageCheck, PackageSearch, CreditCard, Printer, Receipt } from "lucide-react";
import Reveal from "@/components/Reveal";
import useSEO from "@/lib/useSEO";
import { api, formatApiErrorDetail } from "@/lib/api";

const STEPS = [
  { key: "placed", label: "Order placed", icon: Receipt },
  { key: "paid", label: "Paid", icon: CreditCard },
  { key: "production", label: "In production", icon: Printer },
  { key: "shipped", label: "Shipped", icon: Truck },
  { key: "delivered", label: "Delivered", icon: PackageCheck },
];

function stepsFor(order) {
  const paid = order.payment_status === "paid";
  const fs = order.fulfillment_status;
  const inProduction = paid && ["submitted", "draft", "mocked", "submitting", "shipped", "delivered"].includes(fs);
  const shipped = ["shipped", "delivered"].includes(fs);
  const delivered = fs === "delivered";
  const done = [true, paid, inProduction, shipped, delivered];
  // current step = first incomplete step
  const current = done.findIndex((d) => !d);
  return { done, current: current === -1 ? STEPS.length : current };
}

export default function Track() {
  useSEO(
    "Track Your Order | Fleece The Regime",
    "Check your Fleece The Regime order status and tracking number with your order number and email.",
    true
  );
  const [orderNumber, setOrderNumber] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [order, setOrder] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setOrder(null);
    setLoading(true);
    try {
      const res = await api.post("/orders/lookup", { order_number: orderNumber, email });
      setOrder(res.data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="track-page" className="pt-16 min-h-screen">
      <section className="px-6 sm:px-12 lg:px-20 pt-20 pb-14 border-b-2 border-black">
        <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-primary mb-6">Where's my stuff</p>
        <h1 className="font-display uppercase text-6xl sm:text-7xl lg:text-8xl leading-[0.85] tracking-tighter" data-testid="track-heading">
          Track Your<br /><span className="text-primary">Order.</span>
        </h1>
      </section>

      <section className="px-6 sm:px-12 lg:px-20 py-16 max-w-4xl">
        <Reveal>
          <form onSubmit={submit} className="rounded-xl border border-black/15 bg-white p-6 sm:p-8 shadow-hard" data-testid="track-form">
            <p className="font-mono text-sm text-black/60 mb-6">
              Your order number is in the confirmation email — it starts with FTR-. Use the email you ordered with.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="track-order-number" className="block font-mono text-xs font-bold uppercase tracking-widest mb-2">Order number</label>
                <input
                  id="track-order-number"
                  data-testid="track-order-number-input"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  placeholder="FTR-1A2B3C"
                  required
                  className="w-full rounded-md border border-black/25 bg-white px-4 py-3 font-mono text-sm uppercase outline-none focus:border-black focus:shadow-[0_0_0_3px_rgba(255,77,0,0.15)] transition-shadow placeholder:text-black/30 placeholder:normal-case"
                />
              </div>
              <div>
                <label htmlFor="track-email" className="block font-mono text-xs font-bold uppercase tracking-widest mb-2">Email</label>
                <input
                  id="track-email"
                  data-testid="track-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="w-full rounded-md border border-black/25 bg-white px-4 py-3 font-mono text-sm outline-none focus:border-black focus:shadow-[0_0_0_3px_rgba(255,77,0,0.15)] transition-shadow placeholder:text-black/30"
                />
              </div>
            </div>
            {error && (
              <p className="mt-4 font-mono text-xs font-bold text-red-700" data-testid="track-error">{error}</p>
            )}
            <button
              type="submit"
              data-testid="track-submit-button"
              disabled={loading}
              className="mt-6 w-full sm:w-auto flex items-center justify-center gap-2 rounded-lg border-2 border-black bg-primary px-8 py-4 font-display uppercase text-xl text-white shadow-hard btn-press hover:bg-black disabled:opacity-50"
            >
              <PackageSearch className="w-5 h-5" />
              {loading ? "Checking..." : "Find my order"}
            </button>
          </form>
        </Reveal>

        {order && (
          <div className="mt-10 rounded-xl border-2 border-black bg-white p-6 sm:p-8 shadow-hard" data-testid="track-result">
            <div className="flex flex-wrap items-baseline justify-between gap-2 border-b-2 border-black pb-4">
              <p className="font-display uppercase text-2xl" data-testid="track-result-number">{order.order_number}</p>
              <p className="font-mono text-xs text-black/50 uppercase tracking-widest" data-testid="track-result-date">
                Placed {new Date(order.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
              </p>
            </div>

            <ol className="mt-6 space-y-0" data-testid="track-timeline">
              {STEPS.map((step, i) => {
                const { done, current } = stepsFor(order);
                const isDone = i < done.length && done[i];
                const isCurrent = i === current;
                const Icon = step.icon;
                return (
                  <li key={step.key} data-testid={`track-step-${step.key}`} className="flex items-start gap-4">
                    <div className="flex flex-col items-center">
                      <span className={`flex items-center justify-center w-9 h-9 rounded-full border-2 border-black ${isDone ? "bg-primary text-white" : isCurrent ? "bg-white" : "bg-black/5 text-black/30"}`}>
                        <Icon className="w-4 h-4" />
                      </span>
                      {i < STEPS.length - 1 && <span className={`w-0.5 h-6 ${isDone && done[i + 1] ? "bg-primary" : "bg-black/15"}`} />}
                    </div>
                    <div className="pt-2">
                      <p className={`font-mono text-sm font-bold uppercase tracking-wider ${isDone ? "" : isCurrent ? "text-primary" : "text-black/30"}`}>
                        {step.label}
                      </p>
                      {step.key === "production" && isCurrent && (
                        <p className="font-mono text-xs text-black/50 mt-1">Printed to order — production takes 2–7 business days.</p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>

            {!stepsFor(order).done[2] && order.payment_status === "paid" && (
              <p className="mt-6 border-t-2 border-black pt-5 font-mono text-xs text-black/60 leading-relaxed" data-testid="track-cancel-note">
                Changed your mind? Your order is still awaiting personal review and hasn't gone to the printer — cancel free with a full refund until then: email{" "}
                <a href="mailto:yell@fleecetheregime.com" className="font-bold text-black underline underline-offset-2 hover:text-primary transition-colors">
                  yell@fleecetheregime.com
                </a>{" "}
                with your order number. We reply within 24 hours.
              </p>
            )}

            {order.shipments.length > 0 && (
              <div className="mt-6 border-t-2 border-black pt-5" data-testid="track-shipments">
                <p className="font-mono text-xs font-bold uppercase tracking-widest text-black/50 mb-3">Tracking</p>
                {order.shipments.map((s, i) => (
                  <div key={i} className="flex flex-wrap items-center justify-between gap-2 py-2" data-testid={`track-shipment-${i}`}>
                    <span className="font-mono text-sm">{s.carrier || "Carrier"}</span>
                    {s.tracking_url ? (
                      <a href={s.tracking_url} target="_blank" rel="noreferrer" data-testid={`track-shipment-link-${i}`}
                         className="font-mono text-sm font-bold text-primary underline underline-offset-2 hover:text-black transition-colors">
                        {s.tracking_number || "Track package"} &rarr;
                      </a>
                    ) : (
                      <span className="font-mono text-sm font-bold">{s.tracking_number}</span>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 border-t-2 border-black pt-5" data-testid="track-items">
              <p className="font-mono text-xs font-bold uppercase tracking-widest text-black/50 mb-3">In this order</p>
              {order.items.map((it, i) => (
                <div key={i} className="flex justify-between gap-4 py-1 font-mono text-sm" data-testid={`track-item-${i}`}>
                  <span>{it.name}{it.color ? ` — ${it.color}` : ""} / {it.size}</span>
                  <span>× {it.qty}</span>
                </div>
              ))}
            </div>

            <p className="mt-6 font-mono text-xs text-black/50">
              Something look wrong? Email{" "}
              <a href="mailto:yell@fleecetheregime.com" className="underline underline-offset-2 hover:text-primary transition-colors">yell@fleecetheregime.com</a>
              {" "}and we'll sort it out.
            </p>
          </div>
        )}

        {!order && (
          <p className="mt-8 font-mono text-sm text-black/50">
            Have an account?{" "}
            <Link to="/account" data-testid="track-account-link" className="font-bold text-primary underline underline-offset-2 hover:text-black transition-colors">
              Sign in to see your full order history &rarr;
            </Link>
          </p>
        )}
      </section>
    </main>
  );
}
